import CloseIcon from "@mui/icons-material/Close";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { VForm, VMaskTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { globalRemoveMaskReal2 } from "../../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	agreementId: string;
	installment: IAgreementInstallment;
	onClose: () => void;
	onConfirm: () => void;
}

export const ModalRegisterAgencyTransfer = ({ open, agreementId, installment, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [valueInput, setValueInput] = useState("0,00");
	const [formKey, setFormKey] = useState(0);
	const [transferDate, setTransferDate] = useState("");
	const [note, setNote] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const plannedValue = installment.agencyTransferValue ?? 0;

	useEffect(() => {
		if (open) {
			setValueInput(plannedValue > 0 ? plannedValue.toFixed(2).replace(".", ",") : "0,00");
			setFormKey((prev) => prev + 1);
			setTransferDate(format(new Date(), "yyyy-MM-dd"));
			setNote("");
		}
	}, [open, installment.number, plannedValue]);

	const handleConfirm = async () => {
		const cleaned = globalRemoveMaskReal2(String(valueInput || "0"));
		const value = Number(cleaned);

		if (!Number.isFinite(value) || value <= 0) {
			toastHandler.present({ type: "error", messageOrContent: "Informe um valor de repasse válido.", position: "top-right" });
			return;
		}

		if (!transferDate) {
			toastHandler.present({ type: "error", messageOrContent: "Informe a data do repasse.", position: "top-right" });
			return;
		}

		setIsSubmitting(true);
		const result = await SinisterAgreementService.registerAgencyTransfer(agreementId, installment.number, {
			value,
			transferDate,
			note: note || undefined,
		});
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({
			type: "success",
			messageOrContent: `Repasse registrado para a parcela ${installment.number}.`,
			position: "top-right",
		});
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Registrar Repasse
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="info" sx={{ py: 0.5 }}>
							Parcela {installment.number} — {formatScaleValueBR(String(installment.value))} — vencimento em{" "}
							{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}.
							{plannedValue > 0 && ` Repasse planejado para esta parcela: ${formatScaleValueBR(String(plannedValue))}.`}
						</Alert>

						<VForm
							key={formKey}
							onSubmit={() => {}}
							initialData={{ transferValue: valueInput }}
							onPointerEnterCapture={() => {}}
							onPointerLeaveCapture={() => {}}
							placeholder=""
						>
							<VMaskTextField
								fullWidth
								size="small"
								label="Valor do repasse"
								name="transferValue"
								mask="real"
								placeholder="0,00"
								onChange={(e: any) => setValueInput(e?.target?.value ?? "")}
								onBlur={(e: any) => setValueInput(e?.target?.value ?? "")}
								InputProps={{
									startAdornment: <InputAdornment position="start">R$</InputAdornment>,
								}}
							/>
						</VForm>

						<TextField
							type="date"
							fullWidth
							size="small"
							label="Data do repasse"
							value={transferDate}
							onChange={(e) => setTransferDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>

						<TextField
							fullWidth
							size="small"
							multiline
							minRows={2}
							label="Observação (opcional)"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Ex: repasse feito via transferência bancária."
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Button variant="contained" disabled={isSubmitting} onClick={handleConfirm}>
						Confirmar Repasse
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
